import { Animated, NativeScrollEvent, NativeSyntheticEvent, Platform } from 'react-native';
import { ScrollHandlerProcessed, SharedValue, useAnimatedScrollHandler } from 'react-native-reanimated';
import { Route } from 'react-native-tab-view';

import { ScrollLiistenerContext, useScrollLiistenerContext } from '@/contexts/scrollListenerTabViewContext';

type ScrollListenerScenePropsAndRef = {
  /**
   * Disable scroll for unfocused routes is optional,
   * but prevents weird/delayed animations if the user changes tabs
   * and quickly start scrolling the new tab, before
   * the scrollY starting to track the new focused route.
   */
  scrollEnabled: boolean;
  /**
   * Scroll event, enabled only for the focused route.
   */
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  scrollHandler?: ScrollHandlerProcessed<Record<string, unknown>>;
  scrollTopHeader: () => void;
  scrollOffset?: SharedValue<number>;
} & Pick<ScrollLiistenerContext, 'onMomentumScrollBegin' | 'onScrollEndDrag' | 'onMomentumScrollEnd'>;

const useScrollListenerScene = <T extends Route>(routeKey: T['key']): ScrollListenerScenePropsAndRef => {
  const context = useScrollLiistenerContext();

  const { activeRouteKey, scrollY, scrollOffset, ...rest } = context;
  const scrollEnabled = routeKey === activeRouteKey;

  const animatedScrollHandler = useAnimatedScrollHandler({
    onScroll: (event: NativeScrollEvent) => {
      'worklet';
      if (!scrollEnabled || !scrollOffset) return;

      const { contentOffset, contentSize, layoutMeasurement } = event;
      let y = contentOffset.y;

      if (layoutMeasurement) {
        const maxOffsetY = contentSize.height - layoutMeasurement.height;
        // Clamp the value to avoid bottom bounce triggering "scroll up" logic
        if (y > maxOffsetY) {
          y = maxOffsetY;
        }
      }

      if (contentSize.height > 250 && contentOffset.y >= 0) {
        scrollOffset.value = y;
      }
    }
  });

  const scrollHandler = animatedScrollHandler;

  // Animated.event only for updating scrollY (if provided)
  const onScroll =
    scrollEnabled && scrollY
      ? Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
          useNativeDriver: Platform.OS !== 'web'
        })
      : undefined;

  const scrollTopHeader = () => {
    if (scrollY) {
      scrollY.setValue(0);
    }
    if (scrollOffset) {
      scrollOffset.value = 0;
    }
  };

  return {
    scrollEnabled,
    onScroll,
    scrollTopHeader,
    scrollHandler,
    scrollOffset,
    ...rest
  };
};

export default useScrollListenerScene;
